import { Result, failure, success } from '../shared'
import {
  ClientProvider,
  ClientNotesProvider,
  ClientMonitoringsProvider,
  ClientRiskLevelsProvider,
  ClientDelegatesProvider,
  ClientSearchProvider,
  ClientAddFormDataProvider,
  ClientCheckDublicateProvider,
  ClientReqFieldsProvider,
  ClientSelectsProvider,
  ClientRelationsProvider,
  ClientBlacklistProvider,
  ClientWatchlistProvider,
  ClientSmsprovider,
  ClientPermissionsProvider,
  ClientValidatePinProvider,
  ClientIdDocProvider,
  ClientContactVerifyProvider,
  ClientTagProvider,
} from './client.provider'
import {
  ClientServiceErrorCode,
  ClientFailure,
  ClientViewPage,
  ClientLoanStatsServerData,
  ClientNoteAddEditAttributes,
  ClientNoteDeleteAttributes,
  ClientMonitoringAddEditAttributes,
  ClientMonitoringDeleteAttributes,
  ClientMonitoringsPage,
  ClientRiskLevelAddAttributes,
  ClientRiskLevelDeleteAttributes,
  ClientDelegateAddEditAttributes,
  ClientDelegateDeleteAttributes,
  ClientDelegatesPage,
  ClientNotesPage,
  ClientSearchAttributes,
  ClientSearchResult,
  ClientAddParams,
  ClientCheckDublicateParams,
  ClientCheckDublicateResponse,
  ClientGetRequiredFiledsParams,
  ClinetRelationsAddAttributes,
  ClinetRelationsPage,
  ClientRelationsDeleteAttributes,
  ClientAddToBlackListAttributes,
  ClientBlacklistPage,
  ClientRemoveFromBlackListAttributes,
  ClientAddToWatchListAttributes,
  ClientRemoveFromWatchListAttributes,
  ClientWatchlistPage,
  sendSMSParams,
  ClientAddRemovePermissionParams,
  ValidatePinCodeParams,
  ClinetAddIdDocParams,
  ClinetEditIdDocParams,
  ClientDeleteIdDocParams,
  ClientVerifySendSmsParams,
  ClientVerifySMSVerifyParams,
  SendNewPinSMSParams,
  ClientCheckReqFieldsParams,
  ClientTagAddParams,
  ClientDeleteTagParams,
} from './client.types'

export class ClientService {
  constructor(
    private clientProvider: ClientProvider,
    private clientNotesProvider: ClientNotesProvider,
    private clientMonitoringsProvider: ClientMonitoringsProvider,
    private clientRiskLevelsProvider: ClientRiskLevelsProvider,
    private clientDelegatesProvider: ClientDelegatesProvider,
    private clientSearchProvider: ClientSearchProvider,
    private ClientAddFormDataProvider: ClientAddFormDataProvider,
    private ClientCheckDublicateProvider: ClientCheckDublicateProvider,
    private ClientReqFieldsProvider: ClientReqFieldsProvider,
    private ClientSelectsProvider: ClientSelectsProvider,
    private ClientRelationsProvider: ClientRelationsProvider,
    private ClientBlacklistProvider: ClientBlacklistProvider,
    private ClientWatchlistProvider: ClientWatchlistProvider,
    private ClientSmsprovider: ClientSmsprovider,
    private ClientPermissionsProvider: ClientPermissionsProvider,
    private ClientValidatePinProvider: ClientValidatePinProvider,
    private ClientIdDocProvider: ClientIdDocProvider,
    private ClientContactVerifyProvider: ClientContactVerifyProvider,
    private ClientTagProvider: ClientTagProvider,
  ) {}

  private getDetails<T>(page: ClientViewPage, headers?: any) {
    return async (
      clientId: number,
      clientHash: string,
      additionalParams?: { [key: string]: any },
    ): Promise<Result<T, ClientFailure>> => {
      try {
        const { data } = await this.clientProvider.getDetails<T>(clientId, clientHash, page, additionalParams)
        return success(data)
      } catch (error) {
        return failure(ClientServiceErrorCode.GENERAL, {
          message: error.Message || error.Text,
        })
      }
    }
  }

  public async getGeneralDetails<T>(params: {
    clientId: number
    clientHash: string
    page: ClientViewPage
    additionalParams?: { [key: string]: any }
  }) {
    const { clientId, clientHash, page, additionalParams } = params
    try {
      const { data } = await this.clientProvider.getDetails<T>(clientId, clientHash, page, additionalParams)
      return success(data)
    } catch (error) {
      return failure(ClientServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async addEditNote(attributes: ClientNoteAddEditAttributes): Promise<Result<any, ClientFailure>> {
    try {
      const { data } = await this.clientNotesProvider.addEditNote<any>(
        attributes,
        attributes.ID ? ClientNotesPage.Edit : ClientNotesPage.Add,
      )
      return success(data)
    } catch (error) {
      return failure(ClientServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async deleteNote(attributes: ClientNoteDeleteAttributes): Promise<Result<any, ClientFailure>> {
    try {
      const { data } = await this.clientNotesProvider.deleteNote<any>(attributes)
      return success(data)
    } catch (error) {
      return failure(ClientServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async addEditMonitoring(attributes: ClientMonitoringAddEditAttributes): Promise<Result<any, ClientFailure>> {
    try {
      const { data } = await this.clientMonitoringsProvider.addEditMonitoring<any>(
        attributes,
        attributes.ID ? ClientMonitoringsPage.Edit : ClientMonitoringsPage.Add,
      )
      return success(data)
    } catch (error) {
      return failure(ClientServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async deleteMonitoring(attributes: ClientMonitoringDeleteAttributes): Promise<Result<any, ClientFailure>> {
    try {
      const { data } = await this.clientMonitoringsProvider.deleteMonitoring<any>(attributes)
      return success(data)
    } catch (error) {
      return failure(ClientServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async addRiskLevel(attributes: ClientRiskLevelAddAttributes): Promise<Result<any, ClientFailure>> {
    try {
      const { data } = await this.clientRiskLevelsProvider.addRiskLevel<any>(attributes)
      return success(data)
    } catch (error) {
      return failure(ClientServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async deleteRiskLevel(attributes: ClientRiskLevelDeleteAttributes): Promise<Result<any, ClientFailure>> {
    try {
      const { data } = await this.clientRiskLevelsProvider.deleteRiskLevel<any>(attributes)
      return success(data)
    } catch (error) {
      return failure(ClientServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async addEditDelegate(attributes: ClientDelegateAddEditAttributes): Promise<Result<any, ClientFailure>> {
    try {
      const { data } = await this.clientDelegatesProvider.addEditDelegate<any>(
        attributes,
        attributes.ID ? ClientDelegatesPage.Edit : ClientDelegatesPage.Add,
      )
      return success(data)
    } catch (error) {
      return failure(ClientServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async deleteDelegate(attributes: ClientDelegateDeleteAttributes): Promise<Result<any, ClientFailure>> {
    try {
      const { data } = await this.clientDelegatesProvider.deleteDelegate<any>(attributes)
      return success(data)
    } catch (error) {
      return failure(ClientServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async addRelation(attributes: ClinetRelationsAddAttributes): Promise<Result<void, ClientFailure>> {
    try {
      const { data } = await this.ClientRelationsProvider.addRelation<void>(attributes, ClinetRelationsPage.Add)
      return success(data)
    } catch (error) {
      return failure(ClientServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async deleteRelation(attributes: ClientRelationsDeleteAttributes): Promise<Result<void, ClientFailure>> {
    try {
      const { data } = await this.ClientRelationsProvider.deleteRelation<void>(attributes, ClinetRelationsPage.Delete)
      return success(data)
    } catch (error) {
      return failure(ClientServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async addToBlacklist(attribues: ClientAddToBlackListAttributes): Promise<Result<void, ClientFailure>> {
    try {
      const { data } = await this.ClientBlacklistProvider.add<void>(attribues, ClientBlacklistPage.Add)
      return success(data)
    } catch (error) {
      return failure(ClientServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async removeFromBlackList(
    attribues: ClientRemoveFromBlackListAttributes,
  ): Promise<Result<void, ClientFailure>> {
    try {
      const { data } = await this.ClientBlacklistProvider.remove<void>(attribues, ClientBlacklistPage.Remove)
      return success(data)
    } catch (error) {
      return failure(ClientServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async addToWatchlist(attribues: ClientAddToWatchListAttributes): Promise<Result<void, ClientFailure>> {
    try {
      const { data } = await this.ClientWatchlistProvider.add<void>(attribues, ClientWatchlistPage.Add)
      return success(data)
    } catch (error) {
      return failure(error.Code, {
        message: error.Message || error.Text,
      })
    }
  }

  public async removeFromWatchList(
    attribues: ClientRemoveFromWatchListAttributes,
  ): Promise<Result<void, ClientFailure>> {
    try {
      const { data } = await this.ClientWatchlistProvider.remove<void>(attribues, ClientWatchlistPage.Remove)
      return success(data)
    } catch (error) {
      return failure(error.Code, {
        message: error.Message || error.Text,
      })
    }
  }

  public getBaseDetails = this.getDetails<any>(ClientViewPage.Base)
  public getEdit = this.getDetails<any>(ClientViewPage.Edit)
  public getLoans = this.getDetails<any>(ClientViewPage.Loans)
  public getParallelLoans = this.getDetails<any>(ClientViewPage.ParallelLoans)
  public getRelatedLoans = this.getDetails<any>(ClientViewPage.RelatedLoans)
  public getDelegates = this.getDetails<any>(ClientViewPage.Delegates)
  public getActiveNotes = this.getDetails<any>(ClientViewPage.ActiveNotes)
  public getLinkedFiles = this.getDetails<any>(ClientViewPage.LinkedFiles)
  public getRating = this.getDetails<any>(ClientViewPage.Rating)
  public getLoanStats = this.getDetails<ClientLoanStatsServerData>(ClientViewPage.LoanStats)
  public getDynamicFields = this.getDetails<any>(ClientViewPage.DynamicFields)
  public getIdDocs = this.getDetails<any>(ClientViewPage.IdDocs)
  public getCashDocs = this.getDetails<any>(ClientViewPage.CashDocs)
  public getDelegateCashDocs = this.getDetails<any>(ClientViewPage.DelegateCashDocs)
  public getAccounts = this.getDetails<any>(ClientViewPage.Accounts)

  public async clientSearch(params: ClientSearchAttributes): Promise<Result<ClientSearchResult, ClientFailure>> {
    try {
      const { data } = await this.clientSearchProvider.clientSearch<ClientSearchResult>(params)
      return success(data)
    } catch (error) {
      return failure(ClientServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async clientAddFormData(params: Partial<ClientAddParams>): Promise<Result<any, any>> {
    try {
      const { data } = await this.ClientAddFormDataProvider.clientAdd<any>(params)
      return success(data)
    } catch (error) {
      return failure(ClientServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async clientCheckDublicate(
    params: ClientCheckDublicateParams,
  ): Promise<Result<ClientCheckDublicateResponse, any>> {
    try {
      const result = await this.ClientCheckDublicateProvider.clientCheckDublicate<ClientCheckDublicateResponse>(params)
      const { data } = result
      return success(data)
    } catch (error) {
      return failure(ClientServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async getClientReqFields(params: ClientGetRequiredFiledsParams): Promise<Result<any, any>> {
    try {
      const result = await this.ClientReqFieldsProvider.getClientReqFields(params)
      const { data } = result
      return success(data)
    } catch (error) {
      return failure(ClientServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async checkClientReqFields(params: ClientCheckReqFieldsParams): Promise<Result<any, any>> {
    try {
      const result = await this.ClientReqFieldsProvider.checkReqFields(params)
      const { data } = result
      return success(data)
    } catch (error) {
      return failure(ClientServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async getSelects(): Promise<Result<void, any>> {
    try {
      const result = await this.ClientSelectsProvider.getSelects()
      const { data } = result
      return success(data)
    } catch (error) {
      return failure(ClientServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async sendSMS(params: sendSMSParams): Promise<Result<sendSMSParams, any>> {
    try {
      const result = await this.ClientSmsprovider.sendSMS(params)
      const { data } = result
      return success(data)
    } catch (error) {
      return failure(ClientServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async sendNewPinSMS(params: SendNewPinSMSParams): Promise<Result<SendNewPinSMSParams, any>> {
    try {
      const result = await this.ClientSmsprovider.sendNewPinSMS(params)
      const { data } = result
      return success(data)
    } catch (error) {
      return failure(ClientServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async addPermission(
    params: ClientAddRemovePermissionParams,
  ): Promise<Result<ClientAddRemovePermissionParams, any>> {
    try {
      const result = await this.ClientPermissionsProvider.addPermission(params)
      const { data } = result
      return success(data)
    } catch (error) {
      return failure(ClientServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async deletePermission(
    params: ClientAddRemovePermissionParams,
  ): Promise<Result<ClientAddRemovePermissionParams, any>> {
    try {
      const result = await this.ClientPermissionsProvider.deletePermission(params)
      const { data } = result
      return success(data)
    } catch (error) {
      return failure(ClientServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async validatePinCode(params: ValidatePinCodeParams): Promise<Result<ValidatePinCodeParams, any>> {
    try {
      const result = await this.ClientValidatePinProvider.validatePinCode(params)
      const { data } = result
      return success(data)
    } catch (error) {
      return failure(ClientServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async clinetAddIdDoc(params: ClinetAddIdDocParams): Promise<Result<ClinetAddIdDocParams, any>> {
    try {
      const result = await this.ClientIdDocProvider.addIdDoc(params)
      const { data } = result
      return success(data)
    } catch (error) {
      return failure(ClientServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async clinetEditIdDoc(params: ClinetEditIdDocParams): Promise<Result<ClinetEditIdDocParams, any>> {
    try {
      const result = await this.ClientIdDocProvider.editIdDoc(params)
      const { data } = result
      return success(data)
    } catch (error) {
      return failure(ClientServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async clientDeleteIdDoc(params: ClientDeleteIdDocParams): Promise<Result<ClientDeleteIdDocParams, any>> {
    try {
      const result = await this.ClientIdDocProvider.deleteIdDoc(params)
      const { data } = result
      return success(data)
    } catch (error) {
      return failure(ClientServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async clientContactSendSMS(params: ClientVerifySendSmsParams): Promise<Result<ClientDeleteIdDocParams, any>> {
    try {
      const result = await this.ClientContactVerifyProvider.sendSMS(params)
      const { data } = result
      return success(data)
    } catch (error) {
      return failure(ClientServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async clientContactVerifySMS(
    params: ClientVerifySMSVerifyParams,
  ): Promise<Result<ClientVerifySMSVerifyParams, any>> {
    try {
      const result = await this.ClientContactVerifyProvider.verifySMS(params)
      const { data } = result
      return success(data)
    } catch (error) {
      return failure(ClientServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async addClientTag(params: ClientTagAddParams): Promise<Result<unknown, any>> {
    try {
      const result = await this.ClientTagProvider.add(params)
      const { data } = result
      return success(data)
    } catch (error) {
      return failure(ClientServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async deleteClientTag(params: ClientDeleteTagParams): Promise<Result<unknown, any>> {
    try {
      const result = await this.ClientTagProvider.delete(params)
      const { data } = result
      return success(data)
    } catch (error) {
      return failure(ClientServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }
}
